"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, Compass, HardHat, PhoneCall } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const teamMembers = [{
  name: "John Smith",
  role: "Principal Builder",
  license: "",
  bio: "Directs residential builds with focused experience in custom additions, architectural extensions, and structural remodeling across Brookhaven neighborhoods.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_john_smith.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_john_smith.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_john_smith.png-wvc-sizes",
  icon: HardHat
}, {
  name: "Emily Johnson",
  role: "Lead Designer",
  license: "Bespoke Interiors",
  bio: "Focuses on tailored spatial plans, natural light integration, and luxury kitchen and bathroom finishes crafted for modern living.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_emily_johnson.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_emily_johnson.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_emily_johnson.png-wvc-sizes",
  icon: Compass
}, {
  name: "Carlos Ramirez",
  role: "Project Coordinator",
  license: "Site Quality Lead",
  bio: "Directs daily on-site craftsmanship, trades scheduling, and client communication to ensure projects stay prompt, tidy, and built to code.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_carlos_ramirez.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_carlos_ramirez.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/team_carlos_ramirez.png-wvc-sizes",
  icon: Award
}];
export default function Team() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="meet-the-team" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-9 lg:px-16">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <span className="text-primary font-semibold text-xs tracking-[0.14em] uppercase block mb-3">
            LEADERSHIP &amp; CRAFTSMANSHIP
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Meet the team guiding your renovation
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Local builders and designers who manage every detail of your home
            project from initial consultation to final walkthrough.
          </p>
          <div className="h-[1px] bg-primary/40 w-16 mx-auto mt-6" />
        </div>

        {/* Team Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {teamMembers.map((member, i) => {
          const IconComponent = member.icon;
          return <motion.article key={member.name} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
            y: -4
          }} className="group relative rounded-2xl bg-card/85 backdrop-blur-md border border-border/80 shadow-md hover:shadow-xl hover:border-primary/50 transition-[box-shadow,border-color] duration-300 flex flex-col overflow-hidden p-5 sm:p-6">
                {/* Portrait Container */}
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted ring-1 ring-inset ring-black/5 mb-6">
                  <img src={member.image} data-wvc-srcset={member.imageSrcset} data-wvc-sizes={member.imageSizes} alt={`${member.name}, ${member.role} at Brookhaven Elevation Renovations`} loading="lazy" decoding="async" className="h-full w-full object-cover object-top filter grayscale-[15%] contrast-[1.02] group-hover:scale-105 transition-transform duration-500 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-transparent to-transparent pointer-events-none" />

                  {/* Role Floating Badge */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <Badge variant="outline" className="bg-card/90 text-foreground backdrop-blur-sm border-white/40 shadow-xs text-xs font-semibold py-1 px-3">
                      <IconComponent className="size-3 text-primary mr-1" />
                      {member.role}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                        {member.name}
                      </h3>
                      {member.license && <span className="text-xs uppercase tracking-wider text-muted-foreground font-mono">
                          {member.license}
                        </span>}
                    </div>

                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-2">
                      {member.bio}
                    </p>
                  </div>

                  {/* Micro Footer Accent */}
                  <div className="pt-6 mt-6 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-sans">
                    <span className="inline-flex items-center gap-1.5 text-foreground/80 font-medium">
                      <span className="size-1.5 rounded-full bg-primary" />
                      Brookhaven, GA
                    </span>
                    <span className="tracking-wide">Direct Oversight</span>
                  </div>
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Bottom Consultation Banner */}
        <div className="mt-14 md:mt-20 rounded-2xl bg-card border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="text-center sm:text-left">
            <h4 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
              Ready to discuss your home renovation?
            </h4>
            <p className="text-muted-foreground text-sm mt-1">
              Speak directly with our builders and designers about your upcoming project.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="default" size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 font-semibold">
              <Link to="/ContactRequestAnEstimatePage">
                Schedule a consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="rounded-lg px-5 border-border hover:bg-accent hover:text-accent-foreground">
              <a href="tel:+14708479076" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span>(470) 847-9076</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}